/**
 * @fileoverview Feature flags — the pure evaluator.
 * @module lib/features/core/flagEval
 *
 * Split out from `flags.ts` so it can be imported with no runtime attached: the
 * Next.js server bindings evaluate flags during SSR and must not drag the
 * browser transport into a server bundle.
 *
 * ⚠️ MIRROR of `vission_manager/lib/featureFlags/evaluate.ts` (and the copy in
 * flowgrid-core). The browser, the dashboard and the ingest service must all
 * give the same answer for the same visitor — a percentage rollout is only
 * sticky if every one of them buckets identically. Change one, change all three.
 */

// ============================================================================
// EVALUATION — mirror of the dashboard's lib/featureFlags/evaluate.ts
// ============================================================================
//
// Kept in sync deliberately: the browser, flowgrid-core and the dashboard must
// all give the same answer for the same visitor, and a percentage rollout is
// only sticky if every one of them buckets identically.

export type FlagRuleType =
  | 'user' | 'team' | 'website' | 'tier' | 'environment'
  | 'region' | 'sdk_version' | 'percent' | 'attribute' | 'event' | 'all';

export type FlagRuleOperator =
  | 'in' | 'not_in' | 'eq' | 'neq' | 'gte' | 'lte' | 'contains' | 'matches';

/**
 * A value a flag can serve beyond on/off — a string for a copy test, a number
 * for a threshold, an object for a whole config block. Any JSON.
 */
export type FlagVariantValue =
  | string
  | number
  | boolean
  | null
  | FlagVariantValue[]
  | { [key: string]: FlagVariantValue };

/**
 * What kind of question a flag answers.
 *
 *   boolean — a gate. `flags.on(key)`.
 *   variant — an experiment. Named arms, each with a value.
 *   config  — remote configuration. Named arms again, but the value is the point.
 *
 * The evaluator does not branch on this; it exists so the dashboard and the
 * exposure readout can tell "which button copy wins" from "what is the retry
 * limit".
 */
export type FlagType = 'boolean' | 'variant' | 'config';

/** A rule as it arrives in the compiled bundle. Keys are short — this is fetched on cold page loads. */
export interface CompiledRule {
  t: FlagRuleType;
  op?: FlagRuleOperator;
  v?: string | number | boolean | Array<string | number>;
  k?: string;
  pct?: number;
  salt?: string;
  /** `event` rules only: lookback in days. Absent means all time. */
  d?: number;
  /** `true`/`false` on a gate, or a variant key on a multivariate flag. */
  then: boolean | string;
  /**
   * `percent` rules only: a variant split by weight. Decides the arm from a
   * second, independent hash, so raising the rollout adds people without moving
   * anyone already in the experiment to a different arm.
   */
  w?: Record<string, number>;
  /**
   * `all` rules only: conditions that must ALL hold — "plan is pro AND in DE".
   * Each is a rule without its own `then`. An evaluator that predates `all`
   * finds no subject for it and skips the rule, so an older SDK under-enables
   * rather than leaking a feature to people outside the AND.
   */
  c?: CompiledCondition[];
}

/** One condition of an `all` rule — a rule with no outcome of its own. */
export type CompiledCondition = Omit<CompiledRule, 'then' | 'w' | 'c'>;

export interface CompiledFlag {
  on: boolean;
  default: boolean | string;
  rules: CompiledRule[];
  public: boolean;
  /** Absent means `boolean` — every flag compiled before variants existed. */
  type?: FlagType;
  /** Variant key → the value that arm serves. Absent on a plain gate. */
  variants?: Record<string, FlagVariantValue>;
  /**
   * A gate's payload — served whenever a plain on/off flag reads on. Boolean
   * flags only; stored as `variants["true"]` (PostHog's shape), compiled here.
   */
  p?: FlagVariantValue;
  /**
   * Report a per-session exposure for this flag — one row per visitor per
   * session per arm, so the flag can be joined to conversions.
   *
   * Set per flag in the dashboard. Off by default: a row per visitor per flag is
   * the right cost for a flag somebody is measuring and the wrong one for the
   * other thirty-nine.
   */
  x?: boolean;
}

/**
 * A flag read with its reasoning attached — what `flags.explain(key)` returns.
 */
export interface FlagDecision {
  /**
   * The on/off reading. On a gate, the boolean. On a multivariate flag, `true`
   * whenever an arm was assigned unless that arm's value is literally `false` —
   * which is how an experiment says "control means the old behaviour".
   */
  value: boolean;
  /** The assigned arm, or null on a gate and on a kill switch. */
  variant: string | null;
  /** The arm's value. `undefined` when there is none; callers use their default. */
  payload?: FlagVariantValue;
  /** Which step decided it. `override` is a developer forcing it (`flags.override`). */
  reason: 'unknown_flag' | 'kill_switch' | 'rule' | 'default' | 'override';
}

export interface FlagBundle {
  v: number;
  scope: string;
  compiled_at: string;
  flags: Record<string, CompiledFlag>;
  /**
   * Routes on which this site has explicitly switched off feature-flag delivery
   * in its FlowGrid tracking settings. Almost always absent — turning flags off
   * for a page is deliberate, and a blanket "block this page" never does it.
   *
   * On a matching path every `on(key, fallback)` returns `fallback`, exactly as
   * if FlowGrid were unreachable. It is applied here, in the browser, because
   * the delivery endpoint is keyed on the site alone so that a shared cache can
   * serve every visitor the same bytes.
   */
  blocked_routes?: string[];

  /**
   * How this site wants its flags fetched and kept — chosen in the FlowGrid
   * dashboard, compiled into the bundle.
   *
   * **Absent means nobody has chosen**, which is not the same as "chosen the
   * defaults". Absent leaves the app's own `Flowgrid.init({ flags: … })` in
   * charge, and the SDK's built-in default behind that. Present is the dashboard
   * speaking, and it wins — a customer who has to redeploy their app to change a
   * caching policy does not really control it.
   *
   * The evaluator does not read this. It is delivery, not evaluation, and it
   * lives on the type here only because this is where `FlagBundle` is defined —
   * see `flags.ts`, which is the only thing that acts on it.
   */
  delivery?: FlagDelivery;
}

/** See {@link FlagBundle.delivery}. Mirrors `vission_manager/lib/featureFlags/types.ts`. */
export interface FlagDelivery {
  lookup_policy: 'persistenceUntilNetworkSuccess' | 'networkFirst' | 'networkOnly';
  /** Milliseconds a persisted bundle stays usable. */
  persistence_ttl_ms: number;
}

/**
 * Does `pathName` fall under `blockedRoute`?
 *
 * Byte-for-byte the same rule as flowgrid-core's `matchesBlockedRoute`
 * (`src/infrastructure/ingestion/validation.ts`): exact match, or a prefix that
 * ends on a segment boundary — so `/app` blocks `/app/settings` but never
 * `/application`. The two copies must agree, or a page would stop reporting
 * analytics while still serving flags (or the reverse).
 */
export function matchesBlockedRoute(pathName: string, blockedRoute: string): boolean {
  if (blockedRoute === pathName) return true;
  if (!pathName.startsWith(blockedRoute)) return false;
  return blockedRoute.endsWith('/') || pathName.charAt(blockedRoute.length) === '/';
}

/** Is flag delivery switched off for `pathName` by this bundle? */
export function bundleBlocksPath(
  bundle: FlagBundle | null | undefined,
  pathName: string | null | undefined,
): boolean {
  const routes = bundle?.blocked_routes;
  if (!routes || routes.length === 0 || !pathName) return false;
  return routes.some((route) => matchesBlockedRoute(pathName, route));
}

/**
 * What the SDK knows about the current visitor. Everything is optional; a rule
 * whose input is missing simply doesn't match, so a forgotten field
 * under-enables rather than leaking an unreleased feature.
 */
export interface FlagContext {
  /**
   * Your app's own signed-in user — a user id or an email. Only set once someone
   * has authenticated, which is why it can't be the sole bucketing key.
   */
  userId?: string;
  /**
   * FlowGrid's persisted visitor id. `FlowGrid.init()` fills this in for you from
   * the same 365-day identity the rest of the SDK uses, so a percentage rollout
   * splits your anonymous traffic correctly with no work on your part.
   */
  visitorId?: string;
  teamId?: string;
  webId?: string;
  tier?: string;
  /**
   * Which deployment you are running in. `FlowGrid.init()` works this out from
   * the host the SDK was loaded on — localhost and LAN addresses are
   * `development`, the per-branch URLs of the usual hosts and a leading
   * `staging.`/`preview.`/`qa.` label are `preview`, everything else is
   * `production`.
   *
   * Pass it yourself to override the guess — e.g. from `process.env.VERCEL_ENV`,
   * which knows for certain what the hostname only implies. An explicit value is
   * never overwritten.
   */
  environment?: 'development' | 'preview' | 'production';
  /**
   * Where the visitor is.
   *
   * The SDK does not set this and cannot: a browser has no honest answer, and
   * the bundle it downloads is byte-identical for every visitor of your site, so
   * it cannot carry one either. Flags with `region` rules are resolved on
   * FlowGrid's side instead, where the request's origin is actually known — you
   * read them exactly like any other flag and nothing changes for your code.
   */
  region?: string;
  /**
   * Version of this SDK. Filled in from the build constant, so you can hold a
   * feature back until your users have picked up a new release.
   */
  sdkVersion?: string;
  attributes?: Record<string, string | number | boolean>;

  /**
   * What this visitor has already done, as `name` → true.
   *
   * Filled by flowgrid-core when it resolves a flag with an `event` rule, by
   * asking the events pipeline. The browser never sets it: it does not have the
   * visitor's history, and shipping "has purchased" to a page would hand a
   * visitor a read of their own analytics profile.
   *
   * Keys are the token the compiler puts on the rule — the bare event name for
   * an all-time rule, `name@30d` for a windowed one — so one map answers rules
   * with different lookbacks without the evaluator doing any date arithmetic.
   *
   * **`undefined` and `{}` differ.** `undefined` is
   * "nobody asked the events pipeline" and matches nothing, `not_in` included.
   * A populated map is "we looked", so a key that is absent from it means the
   * visitor genuinely has not done that thing, and `not_in` may match.
   */
  events?: Record<string, boolean>;
}

/**
 * What an app may pass as flag context. `events` is left out: only
 * flowgrid-core fills it, from the visitor's real history, for flags that are
 * never sent to a browser or to `getServerFlags` — a value passed in did nothing.
 */
export type FlagInputContext = Omit<FlagContext, 'events'>;

const TIER_ORDER = ['free', 'starter', 'plus', 'pro', 'scale', 'max', 'team', 'business', 'enterprise'];

/** FNV-1a — the same hash the experiments engine uses, so bucketing agrees. */
export function fnv1a(input: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    hash ^= input.charCodeAt(i);
    hash = (hash * 0x01000193) >>> 0;
  }
  return hash;
}

/**
 * Which 1/10000th of the population a subject falls into for a given salt.
 *
 * Exported so the shared test vectors (`tests/flag-vectors.test.ts`) can assert
 * the *bucket*, not merely the boolean that falls out of it. A rollout is only
 * sticky if the dashboard, flowgrid-core and this file all land a given visitor
 * in the same bucket, and asserting the boolean alone would let two of them
 * drift by a bucket and stay green until a customer noticed.
 */
export function bucketOf(subjectId: string, salt: string): number {
  return fnv1a(`${salt}::${subjectId}`) % 10000;
}

/**
 * Which variant a subject gets, given a weight map.
 *
 * Hashed on a **different salt** from the rollout bucket (`{salt}::v`), and that
 * is the point. An experiment needs two properties at once: raising the rollout
 * from 10% to 20% must only ever add people, and it must not move anyone already
 * in the experiment to a different arm. One hash cannot do both — the same
 * number deciding "are you in" and "which side" means widening the gate
 * reshuffles the sides, and an experiment that reshuffles mid-flight has no
 * readable result.
 *
 * Keys are cumulated in sorted order so three implementations land on the same
 * arm. JSON object ordering is not a contract worth betting a rollout on.
 */
export function variantOf(
  subjectId: string,
  salt: string,
  weights: Record<string, number>,
): { variant: string | null; bucket: number } {
  const bucket = bucketOf(subjectId, `${salt}::v`);

  const keys = Object.keys(weights).sort();
  let total = 0;
  for (const key of keys) {
    const weight = weights[key];
    if (typeof weight === 'number' && isFinite(weight) && weight > 0) total += weight;
  }
  if (total <= 0) return { variant: null, bucket };

  let cumulative = 0;
  for (const key of keys) {
    const weight = weights[key];
    if (typeof weight !== 'number' || !isFinite(weight) || weight <= 0) continue;
    cumulative += weight;
    // Rounded for the same reason the rollout threshold is: a boundary that
    // depends on IEEE-754 division is a boundary three implementations will
    // eventually disagree about.
    if (bucket < Math.round((cumulative / total) * 10000)) return { variant: key, bucket };
  }

  // Only reachable through floating-point shortfall on the last boundary. The
  // last positive-weight arm is the honest answer, never null.
  for (let i = keys.length - 1; i >= 0; i--) {
    const weight = weights[keys[i]];
    if (typeof weight === 'number' && isFinite(weight) && weight > 0) {
      return { variant: keys[i], bucket };
    }
  }
  return { variant: null, bucket };
}

function sameId(a: unknown, b: unknown): boolean {
  return String(a).trim().toLowerCase() === String(b).trim().toLowerCase();
}

function compareVersions(a: string, b: string): number {
  const parse = (s: string) => s.replace(/^v/i, '').split(/[.\-+]/).map((p) => parseInt(p, 10) || 0);
  const pa = parse(a);
  const pb = parse(b);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const d = (pa[i] ?? 0) - (pb[i] ?? 0);
    if (d !== 0) return d;
  }
  return 0;
}

function subjectFor(rule: CompiledRule, ctx: FlagContext): string | number | undefined {
  switch (rule.t) {
    case 'user': return ctx.userId;
    case 'team': return ctx.teamId;
    case 'website': return ctx.webId;
    case 'tier': return ctx.tier;
    case 'environment': return ctx.environment;
    case 'region': return ctx.region;
    case 'sdk_version': return ctx.sdkVersion;
    case 'attribute': {
      if (!rule.k) return undefined;
      const raw = ctx.attributes?.[rule.k];
      return typeof raw === 'boolean' ? String(raw) : raw;
    }
    // Most specific identity first. `visitorId` is what makes a rollout work for
    // anonymous traffic: without it the cascade reaches `webId`, which is the
    // same for every visitor of a site, so "10%" would be on for all of them or
    // none. Logging in moves someone from their visitor bucket to their user
    // bucket — accepted, so a rollout follows one person across devices.
    // Handled whole in `ruleMatches` — membership is a set, not a scalar. The
    // sentinel keeps the "missing input never matches" guard from firing before
    // that branch is reached.
    case 'event': return 'event';
    // Only read by a weighted split; the conditions bring their own inputs.
    case 'all':
    case 'percent': return ctx.userId ?? ctx.visitorId ?? ctx.teamId ?? ctx.webId;
  }
}

/**
 * The key an event predicate is stored under in `FlagContext.events`. Bare name
 * for all-time, `name@30d` for a lookback. One definition, so a windowed rule
 * and its resolved answer cannot drift apart.
 */
export function eventToken(name: string, days?: number): string {
  return days && days > 0 ? `${name}@${days}d` : name;
}

function ruleMatches(rule: CompiledRule, ctx: FlagContext, flagKey: string): boolean {
  // `all`: every condition holds — "plan is pro AND in DE".
  if (rule.t === 'all') {
    const conditions = rule.c ?? [];
    return conditions.length > 0 && conditions.every((condition) => ruleMatches({ ...condition, then: rule.then }, ctx, flagKey));
  }

  const subject = subjectFor(rule, ctx);
  if (subject === undefined || subject === null || subject === '') return false;

  if (rule.t === 'event') {
    // "Has this visitor already done X?" — resolved by flowgrid-core and read
    // here as a plain lookup. This never actually runs in a
    // browser: an event rule forces its flag private. It lives here so the three
    // evaluator copies stay in agreement, which the shared fixture checks.
    const held = ctx.events;
    if (!held || typeof held !== 'object') return false;

    const names = Array.isArray(rule.v) ? rule.v : rule.v == null ? [] : [rule.v];
    const did = names.some((n) => held[eventToken(String(n), rule.d)] === true);
    return (rule.op ?? 'in') === 'not_in' ? !did : did;
  }

  if (rule.t === 'percent') {
    const pct = typeof rule.pct === 'number' ? rule.pct : 0;
    if (pct <= 0) return false;
    if (pct >= 100) return true;
    // Rounded, not `pct * 100`: 79.76 * 100 is 7976.000000000001 in binary
    // floating point, which would put bucket 7976 inside a rollout that
    // excludes it. A boundary that depends on IEEE-754 rounding is a boundary
    // three implementations will eventually disagree about.
    return bucketOf(String(subject), rule.salt || flagKey) < Math.round(pct * 100);
  }

  const list = Array.isArray(rule.v) ? rule.v : rule.v == null ? [] : [rule.v as string];

  switch (rule.op ?? 'in') {
    case 'in': return list.some((v) => sameId(v, subject));
    case 'not_in': return !list.some((v) => sameId(v, subject));
    case 'eq': return sameId(rule.v, subject);
    case 'neq': return !sameId(rule.v, subject);
    case 'contains': return String(subject).toLowerCase().includes(String(rule.v).toLowerCase());
    case 'matches':
      try {
        const pattern = String(rule.v ?? '');
        if (!pattern || pattern.length > 200) return false;
        return new RegExp(pattern).test(String(subject));
      } catch { return false; }
    case 'gte':
    case 'lte': {
      let cmp: number;
      if (rule.t === 'tier') {
        const a = TIER_ORDER.indexOf(String(subject));
        const b = TIER_ORDER.indexOf(String(rule.v));
        if (a < 0 || b < 0) return false;
        cmp = a - b;
      } else if (rule.t === 'sdk_version') {
        cmp = compareVersions(String(subject), String(rule.v));
      } else {
        cmp = Number(subject) - Number(rule.v);
        if (isNaN(cmp)) return false;
      }
      return (rule.op ?? 'in') === 'gte' ? cmp >= 0 : cmp <= 0;
    }
    default: return false;
  }
}

/**
 * Turn a decided `then` (or a flag's default) into the three things a caller can
 * ask for: the on/off reading, the arm, and the arm's value.
 *
 * One rule, and every other read follows from it: **a flag is on when an arm was
 * assigned, unless that arm's value is literally `false`.** That is what lets
 * one engine answer a gate, an experiment and a config block without the caller
 * having to know which it is asking.
 */
function resolveThen(
  then: boolean | string,
  flag: CompiledFlag,
): { value: boolean; variant: string | null; payload?: FlagVariantValue } {
  // A gate — and the shape of every flag compiled before variants existed,
  // which is why no cached bundle needed rewriting when they arrived.
  if (typeof then === 'boolean') {
    if (then && flag.p !== undefined) return { value: true, variant: null, payload: flag.p };
    return { value: then, variant: null };
  }
  const payload = flag.variants?.[then];
  return { value: payload !== false, variant: then, payload };
}

/**
 * Evaluate one flag, with its reasoning. Pure: master switch → rules in order,
 * first match wins → the flag's default.
 *
 * `fallback` covers a flag this bundle has never heard of — your code's own
 * default, because new code running against old config is normal.
 */
export function decideFlag(
  bundle: FlagBundle | null,
  key: string,
  ctx: FlagContext,
  fallback: boolean,
): FlagDecision {
  const flag = bundle?.flags?.[key];
  if (!flag) return { value: fallback, variant: null, reason: 'unknown_flag' };
  // A kill switch is off, full stop — no arm, no value. Whatever an experiment
  // was serving, "turn it off" has to mean the code's own path back.
  if (!flag.on) return { value: false, variant: null, reason: 'kill_switch' };

  for (const rule of flag.rules) {
    if (!ruleMatches(rule, ctx, key)) continue;

    // A weighted percent rule decides the arm itself, from its own hash; `then`
    // is not consulted. The one place a rule yields something it doesn't contain.
    if (rule.w && (rule.t === 'percent' || rule.t === 'all')) {
      const subject = subjectFor(rule, ctx);
      const assigned = variantOf(String(subject ?? ''), rule.salt || key, rule.w);
      if (assigned.variant !== null) {
        const payload = flag.variants?.[assigned.variant];
        return { value: payload !== false, variant: assigned.variant, payload, reason: 'rule' };
      }
      // An unusable split (empty, or every weight zero) falls through to `then`
      // rather than inventing an arm.
    }

    return { ...resolveThen(rule.then, flag), reason: 'rule' };
  }

  return { ...resolveThen(flag.default, flag), reason: 'default' };
}

/**
 * Is this flag on? The boolean read, and the one almost every caller wants.
 */
export function evaluateFlag(
  bundle: FlagBundle | null,
  key: string,
  ctx: FlagContext,
  fallback: boolean,
): boolean {
  return decideFlag(bundle, key, ctx, fallback).value;
}

